import { environmentLabel, featureLabel, serviceLabel } from 'lib';
import { useTranslation } from 'react-i18next';
import { TAB } from './helpers';

const inputCls =
  'h-[34px] border border-[#D7DEE8] rounded-lg px-3 py-0 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#334155]/20';
const labelCls = 'text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide';

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const Select = ({ value, onChange, options, allLabel, format }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
    <option value="all">{allLabel}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {format ? format(opt) : opt}
      </option>
    ))}
  </select>
);

const FilterBar = ({ tab, filters, options, onChange, onApply, onClear, onExport, exporting }) => {
  const { t } = useTranslation();
  const showCallFilters = tab === TAB.CALLS;
  const showRunFilters = tab === TAB.RUNS;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 mb-4">
      <div className="flex flex-wrap gap-3 items-end">
        <Field label={t('admin.activityLog.filters.from', { defaultValue: 'From' })}>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => onChange('from', e.target.value)}
            className={inputCls}
          />
        </Field>

        <Field label={t('admin.activityLog.filters.to', { defaultValue: 'To' })}>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => onChange('to', e.target.value)}
            className={inputCls}
          />
        </Field>

        {/*
          The environment filter is offered whenever the store holds more than one identity. A
          store normally holds exactly one — the environments have separate databases — so showing
          it unconditionally would offer a filter that can only ever return nothing. More than one
          means a configuration change or a mislabelled deploy, and then it earns its place.
        */}
        {options.environments?.length > 1 && (
          <Field
            label={t('admin.activityLog.filters.environment', { defaultValue: 'Environment' })}
          >
            <Select
              value={filters.environment}
              onChange={(v) => onChange('environment', v)}
              options={options.environments}
              allLabel={t('admin.activityLog.filters.allEnvironments', {
                defaultValue: 'All environments'
              })}
              format={environmentLabel}
            />
          </Field>
        )}

        {showCallFilters && (
          <>
            <Field label={t('admin.activityLog.filters.service', { defaultValue: 'Service' })}>
              <Select
                value={filters.service}
                onChange={(v) => onChange('service', v)}
                options={options.services || []}
                allLabel={t('admin.activityLog.filters.allServices', {
                  defaultValue: 'All services'
                })}
                format={serviceLabel}
              />
            </Field>

            <Field label={t('admin.activityLog.filters.feature', { defaultValue: 'Feature' })}>
              <Select
                value={filters.feature}
                onChange={(v) => onChange('feature', v)}
                options={options.features || []}
                allLabel={t('admin.activityLog.filters.allFeatures', {
                  defaultValue: 'All features'
                })}
                format={featureLabel}
              />
            </Field>

            <Field label={t('admin.activityLog.filters.outcome', { defaultValue: 'Outcome' })}>
              <Select
                value={filters.outcome}
                onChange={(v) => onChange('outcome', v)}
                options={options.callOutcomes || []}
                allLabel={t('admin.activityLog.filters.allOutcomes', {
                  defaultValue: 'All outcomes'
                })}
              />
            </Field>

            <Field label={t('admin.activityLog.filters.subject', { defaultValue: 'Fixture / ID' })}>
              <input
                type="text"
                value={filters.subjectId}
                onChange={(e) => onChange('subjectId', e.target.value)}
                placeholder={t('admin.activityLog.filters.subjectHint', {
                  defaultValue: 'Trace one fixture'
                })}
                className={`${inputCls} w-[170px]`}
              />
            </Field>
          </>
        )}

        {showRunFilters && (
          <>
            <Field label={t('admin.activityLog.filters.job', { defaultValue: 'Job' })}>
              <Select
                value={filters.job}
                onChange={(v) => onChange('job', v)}
                options={options.jobs || []}
                allLabel={t('admin.activityLog.filters.allJobs', { defaultValue: 'All jobs' })}
              />
            </Field>

            <Field label={t('admin.activityLog.filters.outcome', { defaultValue: 'Outcome' })}>
              <Select
                value={filters.outcome}
                onChange={(v) => onChange('outcome', v)}
                options={options.runOutcomes || []}
                allLabel={t('admin.activityLog.filters.allOutcomes', {
                  defaultValue: 'All outcomes'
                })}
              />
            </Field>
          </>
        )}

        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            onClick={onApply}
            className="h-[34px] px-4 rounded-lg bg-[#334155] text-white text-[13px] font-medium hover:bg-[#1E293B] transition-colors"
          >
            {t('admin.activityLog.filters.apply', { defaultValue: 'Apply' })}
          </button>
          <button
            type="button"
            onClick={onClear}
            className="h-[34px] px-4 rounded-lg border border-[#D7DEE8] text-[#475569] text-[13px] font-medium hover:bg-[#F1F5F9] transition-colors"
          >
            {t('admin.activityLog.filters.clear', { defaultValue: 'Clear' })}
          </button>
          {!showCallFilters && !showRunFilters ? null : (
            <button
              type="button"
              onClick={onExport}
              disabled={exporting}
              className="h-[34px] px-4 rounded-lg border border-[#D7DEE8] text-[#475569] text-[13px] font-medium hover:bg-[#F1F5F9] transition-colors disabled:opacity-40"
            >
              {exporting
                ? t('admin.activityLog.filters.exporting', { defaultValue: 'Exporting…' })
                : t('admin.activityLog.filters.export', { defaultValue: 'Export CSV' })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
